import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const typeLabels = {
  cp: 'Congé payé', absence_np: 'Absence non payée',
  arret: 'Arrêt maladie', formation: 'Formation',
};
const typeColors = {
  cp: 'bg-emerald-50 text-emerald-800 border-emerald-400',
  absence_np: 'bg-rose-50 text-rose-700 border-rose-300',
  arret: 'bg-amber-50 text-amber-800 border-amber-400',
  formation: 'bg-blue-50 text-[#002FA7] border-[#002FA7]',
};

export default function Absences() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ salarie_id: '', type: 'cp', date_debut: '', date_fin: '', commentaire: '' });

  const load = async () => {
    const ps = [api.get('/absences')];
    if (isAdmin) ps.push(api.get('/users'));
    const [a, s] = await Promise.all(ps);
    setList(a.data);
    if (isAdmin && s) setSalaries(s.data.filter(u => u.role === 'salarie' || u.role === 'admin'));
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/absences', { ...form, salarie_id: isAdmin ? form.salarie_id : user.id });
      toast.success('Absence enregistrée');
      setOpen(false);
      setForm({ salarie_id: '', type: 'cp', date_debut: '', date_fin: '', commentaire: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    await api.delete(`/absences/${id}`); toast.success('Supprimé'); load();
  };

  const salarieMap = Object.fromEntries(salaries.map(s => [s.id, s]));

  return (
    <Layout
      title={isAdmin ? 'Absences' : 'Mes absences'}
      subtitle="Disponibilités des équipes"
      actions={
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="nouvelle-absence-btn">
              <Plus size={14} className="mr-2" /> Nouvelle absence
            </Button>
          </SheetTrigger>
          <SheetContent className="rounded-none w-full sm:max-w-md">
            <SheetHeader>
              <SheetTitle className="font-display font-bold text-2xl tracking-tight">Nouvelle absence</SheetTitle>
            </SheetHeader>
            <form onSubmit={submit} className="space-y-4 mt-6">
              {isAdmin && (
                <div>
                  <Label>Salarié *</Label>
                  <Select value={form.salarie_id} onValueChange={(v) => setForm({ ...form, salarie_id: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Début *</Label>
                  <Input required type="date" className="rounded-none font-mono" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} />
                </div>
                <div>
                  <Label>Fin *</Label>
                  <Input required type="date" className="rounded-none font-mono" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Commentaire</Label>
                <Input className="rounded-none" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} />
              </div>
              <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]">Enregistrer</Button>
            </form>
          </SheetContent>
        </Sheet>
      }
    >
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="absences-table">
          <thead className="border-b border-zinc-900">
            <tr className="text-left">
              {isAdmin && <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Salarié</th>}
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Type</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Début</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Fin</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Commentaire</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                {isAdmin && <td className="px-4 py-3 font-medium">{salarieMap[a.salarie_id] ? `${salarieMap[a.salarie_id].prenom} ${salarieMap[a.salarie_id].nom}` : '—'}</td>}
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${typeColors[a.type]}`}>
                    {typeLabels[a.type]}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{a.date_debut}</td>
                <td className="px-4 py-3 font-mono text-xs">{a.date_fin}</td>
                <td className="px-4 py-3 text-zinc-600 text-xs">{a.commentaire || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(a.id)} className="p-1.5 hover:bg-rose-100 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={isAdmin ? 6 : 5} className="px-4 py-12 text-center text-zinc-500">Aucune absence.</td></tr>}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
